import { getBackendUrl } from '@/lib/backendUrl';
import { getApiError } from '@/lib/utils';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Zap, SearchX, Building2, Package, Users } from 'lucide-react';
import { toast } from 'sonner';
import MonthShortcuts from './MonthShortcuts';
import { QboTargetPicker } from './QboTargetPicker';

const API = getBackendUrl();

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const KIND_META = {
  site_customer: { label: 'Customer', icon: Building2, listType: 'customer' },
  service_item: { label: 'Item', icon: Package, listType: 'item' },
  auditor_class: { label: 'Class', icon: Users, listType: 'class' },
};

function slug(s) {
  return String(s).replace(/[^a-zA-Z0-9]+/g, '-');
}

function formatDateOnly(isoDateStr) {
  if (!isoDateStr) return null;
  const [y, m, d] = isoDateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB');
}

export default function DecisionQueuePanel() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState({ customer: [], item: [], class: [] });
  const [highlightKey, setHighlightKey] = useState(null);
  const datesEditedRef = useRef(false);
  const queueRequestIdRef = useRef(0);

  const loadCandidates = useCallback(() => {
    Promise.all(['customer', 'item', 'class'].map((lt) =>
      axios.get(`${API}/api/sales-invoicing/reference-lists/${lt}`, { headers: authHeaders() }).then((r) => [lt, r.data.items || []])
    )).then((pairs) => {
      const next = { customer: [], item: [], class: [] };
      pairs.forEach(([lt, items]) => { next[lt] = items.filter((i) => i.is_active && i.qbo_status !== 'ambiguous'); });
      setCandidates(next);
    }).catch((err) => toast.error(getApiError(err, 'Could not load QBO reference lists.')));
  }, []);

  const loadQueue = useCallback(async (from, to) => {
    const requestId = ++queueRequestIdRef.current;
    setLoading(true);
    try {
      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const { data } = await axios.get(`${API}/api/sales-invoicing/decision-queue`, { params, headers: authHeaders() });
      if (requestId !== queueRequestIdRef.current) return; // a newer request has since started — this response is stale
      if (!datesEditedRef.current) {
        setFromDate(data.from_date);
        setToDate(data.to_date);
      }
      setJobs(data.jobs || []);
    } catch (err) {
      if (requestId !== queueRequestIdRef.current) return;
      setJobs([]);
      toast.error(getApiError(err, 'Failed to load the decision queue.'));
    } finally {
      if (requestId === queueRequestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { loadCandidates(); loadQueue(undefined, undefined); }, [loadCandidates, loadQueue]);

  const editDate = (setter) => (e) => { datesEditedRef.current = true; setter(e.target.value); };
  const applyFilters = () => { datesEditedRef.current = true; loadQueue(fromDate, toDate); };
  const applyShortcut = (from, to) => {
    datesEditedRef.current = true;
    setFromDate(from);
    setToDate(to);
    loadQueue(from, to);
  };

  const totalItems = jobs.reduce((n, j) => n + j.items.length, 0);

  const advanceTo = (jobId, sourceKey) => {
    const key = `dq-item-${jobId}-${slug(sourceKey)}`;
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${key}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightKey(key);
        setTimeout(() => setHighlightKey((k) => (k === key ? null : k)), 1600);
      }
    }, 50);
  };

  const onDecided = (jobId, sourceKey) => {
    // Remove this item (and the job card, if it was the last item on that
    // job) from the local queue — no full reload needed — then advance
    // focus to whatever is now the next remaining item.
    let flat = [];
    jobs.forEach((j) => j.items.forEach((it) => flat.push({ jobId: j.job_id, sourceKey: it.source_key })));
    const idx = flat.findIndex((f) => f.jobId === jobId && f.sourceKey === sourceKey);
    const next = idx >= 0 ? flat[idx + 1] : null;

    setJobs((prev) => prev
      .map((j) => (j.job_id === jobId ? { ...j, items: j.items.filter((it) => it.source_key !== sourceKey) } : j))
      .filter((j) => j.items.length > 0));

    if (next) advanceTo(next.jobId, next.sourceKey);
  };

  return (
    <div data-testid="decision-queue-panel">
      <div className="rounded-lg border border-sky-200 bg-sky-50 text-sky-900 text-sm px-4 py-3 mb-4" data-testid="decision-queue-scope-notice">
        Every job needing a customer, item or class decision in this range, grouped together so you don't have to
        flip between tabs. Each row shows the source, any suggested target with its confidence, a searchable QBO
        picker, and one deliberate "Resolve" action — nothing saves until you click it. There is no "accept all".
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="decision-queue-filters">
        <div>
          <Label htmlFor="dq-from-date" className="text-xs text-slate-500">From</Label>
          <Input id="dq-from-date" type="date" value={fromDate} onChange={editDate(setFromDate)} data-testid="decision-queue-from-date" className="w-[160px]" />
        </div>
        <div>
          <Label htmlFor="dq-to-date" className="text-xs text-slate-500">To</Label>
          <Input id="dq-to-date" type="date" value={toDate} onChange={editDate(setToDate)} data-testid="decision-queue-to-date" className="w-[160px]" />
        </div>
        <Button size="sm" onClick={applyFilters} data-testid="decision-queue-apply-btn">Apply</Button>
        <MonthShortcuts onSelect={applyShortcut} testIdPrefix="decision-queue" />
        <span className="text-sm text-slate-500 ml-auto" data-testid="decision-queue-remaining-count">
          {loading ? 'Loading…' : `${totalItems} decision(s) across ${jobs.length} job(s)`}
        </span>
      </div>

      {!loading && jobs.length === 0 && (
        <div className="text-center text-slate-500 py-10" data-testid="decision-queue-empty">
          <SearchX className="h-6 w-6 mx-auto mb-2 text-slate-300" />
          Nothing needs a decision in this range.
        </div>
      )}

      <div className="space-y-3">
        {jobs.map((job) => (
          <div key={job.job_id} className="rounded-lg border border-slate-200 p-2.5" data-testid={`dq-job-${job.job_id}`}>
            <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
              <div className="font-medium text-slate-800">{job.hotel_name || '—'} <span className="text-slate-400 font-normal text-sm">({job.job_reference})</span></div>
              <div className="text-sm text-slate-500">{formatDateOnly(job.scheduled_date)}</div>
            </div>
            <div className="space-y-1.5">
              {job.items.map((item) => (
                <DecisionItemRow
                  key={item.source_key}
                  jobId={job.job_id}
                  item={item}
                  candidates={candidates[KIND_META[item.kind].listType]}
                  highlighted={highlightKey === `dq-item-${job.job_id}-${slug(item.source_key)}`}
                  onDecided={() => onDecided(job.job_id, item.source_key)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DecisionItemRow({ jobId, item, candidates, highlighted, onDecided }) {
  const meta = KIND_META[item.kind];
  const Icon = meta.icon;
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(item.strong_suggestion ? item.suggested.reference_id : null);
  const [selectedName, setSelectedName] = useState(item.strong_suggestion ? item.suggested.reference_name : null);
  const testKey = `dq-item-${jobId}-${slug(item.source_key)}`;
  const confidencePct = item.suggested ? Math.round(item.suggested.confidence * 100) : null;
  const isOriginalSuggestion = item.suggested && selectedId === item.suggested.reference_id;

  const resolve = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/sales-invoicing/workbench/${item.kind}/decide`, {
        source_key: item.source_key, resolved_reference_id: selectedId,
      }, { headers: authHeaders() });
      toast.success(`Saved — ${item.label} resolved.`);
      onDecided();
    } catch (err) {
      toast.error(getApiError(err, 'Could not save this decision.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={testKey}
      className={`grid grid-cols-1 md:grid-cols-[260px_1fr_230px_110px] items-start md:items-center gap-1.5 md:gap-3 rounded-md border p-2 transition-all ${highlighted ? 'border-sky-400 ring-2 ring-sky-300 bg-sky-50' : 'border-slate-100'}`}
    >
      <div className="flex items-start md:items-center gap-2 min-w-0">
        <Icon className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5 md:mt-0" />
        <Badge variant="secondary" className="flex-shrink-0">{meta.label}</Badge>
        <span className="text-sm font-medium break-words">{item.label || '—'}</span>
      </div>

      <div className="min-w-0" data-testid={`${testKey}-suggestion`}>
        {item.suggested ? (
          <Badge
            variant="secondary"
            className={`font-normal whitespace-normal break-words h-auto py-1 ${confidencePct >= 85 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}
            title={`${item.suggested.reference_name} — ${confidencePct}% match`}
          >
            {item.suggested.source === 'duplicate_reuse'
              ? `Reuse: ${item.suggested.reference_name} — same name already confirmed elsewhere`
              : `${item.suggested.reference_name} (${confidencePct}%)`}
          </Badge>
        ) : (
          <span className="text-xs text-slate-400">No suggestion — search to pick a target</span>
        )}
      </div>

      <QboTargetPicker
        candidates={candidates}
        disabled={saving}
        selectedName={selectedName}
        onPick={(id, name) => { setSelectedId(id); setSelectedName(name); }}
        testKey={testKey}
      />

      <Button
        size="sm"
        variant={isOriginalSuggestion && item.strong_suggestion ? 'default' : 'outline'}
        disabled={saving || !selectedId}
        onClick={resolve}
        data-testid={`${testKey}-resolve-btn`}
        title={selectedId ? `Resolve to: ${selectedName}` : 'Pick a target first'}
      >
        {isOriginalSuggestion && item.strong_suggestion && <Zap className="h-3.5 w-3.5 mr-1" />}
        Resolve
      </Button>
    </div>
  );
}

